import { useState, useEffect, useRef } from 'react';
import { calculateAnnualFinancialSummary, 
  getVal, 
  setBool, 
  isNegative,
  formatDate 
} from '../../helpers/process_morningstar.js';

import {
  Box,
  Container,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper
} from "@mui/material";


export default function AnnualAssetLife() {
  const tableRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [currentTableData, setCurrentTableData] = useState([]);
  const [Tax, setTax] = useState(getVal("selectedTax"));
  const [capRD, setCapRD] = useState(getVal("capRD"));
  const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));

  useEffect(() => {
    if (tableRef.current) {
      const el = tableRef.current;
      el.scrollTo({
        left: el.scrollWidth,
      });
    }
  }, [currentTableData]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "includeOtherIncome") setIncludeOtherIncome(JSON.parse(event.newValue));
    };
  
    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIncludeOtherIncome(getVal("includeOtherIncome"));
    };
  
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);
  
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const financialSummaryData = await calculateAnnualFinancialSummary(capRD, includeOtherIncome, Tax);
      setCurrentTableData(financialSummaryData);
    };

    fetchData();
  }, [capRD, includeOtherIncome, Tax]);

  // Helper to update table data
  const updateFinancialSummaryTable = (newData) => {
    setTables(prev =>
      prev.map(t =>
        t.tableName === "Invested Capital" ? { ...t, tableData: newData } : t
      )
    );
    setCurrentTableData(newData);
  };

  if (error) return <div>Error: {error}</div>;

      return (
        <>
            <>

              {currentTableData.length > 0 ? (
                <Box sx={{ mx: "25px" }}>
               <TableContainer component={Paper} sx={{ boxShadow: "none", pb: 10 }} ref={tableRef}>

                {/* Excluding Intangibles */}
                <Table stickyHeader size="small">
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB",
                            borderRight: "1px solid #969696ff",
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 230  
                          }}
                        >
                          Excluding Intangibles
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176",
                              borderRight: "1px solid #969696ff", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 65.53
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                        { label: "Gross Fixed Assets (Excl. Intangibles)", field: "grossFixedAssets" },
                        { label: "Other Non-Current Assets", field: "nonCurrentAssetsLife" },
                        { label: "Inventory", field: "inventory" },
                        { label: "Depreciating Asset (Life > 1 Year)", field: "depreciatingAssetLife" },
                        { label: "", field: "" },
                        { label: "Depreciation", field: "depreciation" },
                        { label: "", field: "" },
                        { label: "Data", field: "data" },
                        { label: "Number of periods with data", field: "numOfPeriods" },
                        { label: "Asset Life (Annualized)", field: "assetLifeAnnual" },
                        { label: "Asset Life (3 year moving average)", field: "assetLife3Year" },
                        { label: "Asset Life (Excl. Intangibles)", field: "assetLifeExclIntangible" },
                        { label: "" , field: "" },
                    ].map((rowItem, rowIdx) => (
                      <TableRow
                        key={rowIdx}
                        sx={{
                          backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",  
                          }}
                        >
                          {rowItem.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff", 
                            color: isNegative(row[rowItem.field]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {row[rowItem.field]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Including Intangibles */}
                <Table stickyHeader size="small">
                  <TableHead>
                      {[{ value : "Date", time: "Time" }].map((item, idx) => (
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: "#F9FAFB", 
                            borderRight: "1px solid #969696ff", 
                            fontWeight: "bold", 
                            color: "black",
                            position: "sticky", 
                            left: 0, 
                            zIndex: 3,
                            minWidth: 230 
                          }}
                        >
                          Including Intangibles
                        </TableCell>
                        {currentTableData.map((row, colIdx) => (
                          <TableCell sx={{backgroundColor: row[item.time] === "Annual" ? "#324052" : "#FFF176", 
                              borderRight: "1px solid #969696ff", 
                              color: row[item.time] === "Annual" ? "white" : "black", 
                              whiteSpace: 'nowrap',
                              padding: 0,
                              minWidth: 65.53
                            }} 
                              key={colIdx}
                          >
                            <div style={{ textAlign: "center", fontSize: "0.67rem" }}>{row[item.time]}</div>
                            <div style={{ 
                              textAlign: "center",
                              fontWeight: "bold",
                              textDecoration: "underline",
                              fontSize: "0.67rem"
                              }}
                            >
                              {formatDate(row[item.value])}
                            </div>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableHead>
                  <TableBody>
                    {[
                        { label: "Gross Fixed Assets (Incl. Intangibles)", field: "grossFixedAssetsInclIntangible" },
                        { label: "Other Non-Current Assets", field: "nonCurrentAssetsLife" },
                        { label: "Inventory", field: "inventory" },
                        { label: "Depreciating Asset (Life > 1 Year)", field: "depreciatingAssetLifeInclIntangible" },
                        { label: "", field: "" },
                        { label: "Depreciation", field: "depreciation" },
                        { label: "", field: "" },
                        { label: "Data", field: "data" },
                        { label: "Number of periods with data", field: "numOfPeriods" },
                        { label: "Asset Life (Annualized)", field: "assetLifeAnnualInclIntangible" },
                        { label: "Asset Life (3 year moving average)", field: "assetLife3YearInclIntangible" },
                        { label: "Asset Life (Incl. Intangibles)", field: "assetLifeInclIntangible" },
                        { label: "" , field: "" },
                        { label: "Asset Life (Used)", field: "assetLifeExclIntangible" },
                    ].map((rowItem, rowIdx) => (
                      <TableRow
                        key={rowIdx}
                        sx={{
                          backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          "& td": { fontSize: "0.67rem", borderBottom: "1px solid #E5E7EB", padding: "1px 8px" },
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {/* Render the row label as first cell */}
                        <TableCell sx={{ 
                          fontWeight: 'bold',
                          position: "sticky",
                          left: 0, 
                          zIndex: 2,
                          height: 15,
                          backgroundColor: rowIdx % 2 === 0 ? "#ffffff" : "#F9FAFB",
                          borderRight: "1px solid #969696ff",  
                          }}
                        >
                          {rowItem.label}
                        </TableCell>

                        {/* Render the financial data cells */}
                        {currentTableData.map((row, colIdx) => (
                          <TableCell key={colIdx} sx={{ 
                            borderRight: "1px solid #969696ff", 
                            color: isNegative(row[rowItem.field]) ? "red" : "inherit",
                            textAlign: "right"
                          }}>
                            {row[rowItem.field]}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "50vh", // full screen height
                    }}
                  >
                    <h3>Loading...</h3>
                    <CircularProgress />
                  </div>
            )}
        </>
    </>
  )}